/**
 * Where Causee will post to and from (P25028-126).
 *
 * The United Kingdom only, and that is a deliberate limit rather than an
 * oversight.
 *
 * Shippo will happily quote an international parcel — £21.97 to the United
 * States, £16.25 to Ireland — so without this a buyer abroad could order and be
 * charged a sensible-looking price. The parcel would then very likely fail,
 * because anything leaving the UK needs a customs declaration and we do not
 * produce one. Charging for a shipment that cannot lawfully be posted is worse
 * than declining it.
 *
 * It also settles the Crown Dependencies without a separate rule: Jersey,
 * Guernsey and the Isle of Man are `JE`, `GG` and `IM`, not `GB`, so they fall
 * outside this list. They are outside the UK customs area and would need the
 * same declaration.
 *
 * The server does not trust this. `isSupportedShippingCountry` in
 * `server/api-util/shippo.js` holds the same list and is the one that decides,
 * because the endpoints are reachable without these forms. Keep the two in step.
 *
 * This governs **delivery and posting addresses only**. A billing address is
 * left alone on purpose — a card registered abroad can perfectly well pay for a
 * parcel sent within the UK.
 */
export const SHIPPING_COUNTRY_CODES = ['GB'];

/**
 * The country a delivery or posting address starts on (P25028-267).
 *
 * Set only while there is a single country to choose — a dropdown with one option
 * is a step that asks nothing. Should the list ever grow, the forms go back to
 * asking, rather than quietly guessing one country among several.
 */
export const DEFAULT_SHIPPING_COUNTRY =
  SHIPPING_COUNTRY_CODES.length === 1 ? SHIPPING_COUNTRY_CODES[0] : undefined;

/** The country options a delivery or posting address may offer. */
export const shippingCountries = countryCodes =>
  countryCodes.filter(country => SHIPPING_COUNTRY_CODES.includes(country.code));

/**
 * What is wrong with a postcode for the chosen country, if anything.
 *
 * The browser twin of `postcodeProblemFor` in `server/api-util/shippo.js`, so the
 * form can say so as the buyer types. The server does not trust this one and
 * checks again before any carrier is asked. Keep the two in step.
 *
 * Without it, a Malaysian address with United Kingdom picked from the dropdown was
 * priced and could be paid for: the country passed, and Shippo quoted the parcel
 * anyway, for whichever half of the address it chose to believe.
 *
 * Returns null when it is fine, 'invalid' when it is not a postcode of that
 * country, or 'outside-uk' for Jersey, Guernsey and Isle of Man postcodes, which
 * look like UK ones but are outside the area Causee posts to. An empty postcode or
 * country is left to the required-field checks.
 */
// Outward part (A9, A99, A9A, AA9, AA99, AA9A) + inward part (9AA), with the
// space taken out. The inward letters never use C, I, K, M, O or V.
const UK_POSTCODE = /^(GIR0AA|[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][ABD-HJLNP-UW-Z]{2})$/;
const CROWN_DEPENDENCY_POSTCODE = /^(JE|GY|IM)[0-9]/;
const POSTCODE_PATTERNS = { GB: UK_POSTCODE };

export const postcodeProblemFor = (postcode, country) => {
  const code = String(country || '').trim().toUpperCase();
  const normalized = String(postcode || '').replace(/\s+/g, '').toUpperCase();
  const pattern = POSTCODE_PATTERNS[code];
  if (!normalized || !pattern) {
    return null;
  }
  if (!pattern.test(normalized)) {
    return 'invalid';
  }
  if (code === 'GB' && CROWN_DEPENDENCY_POSTCODE.test(normalized)) {
    return 'outside-uk';
  }
  return null;
};

/**
 * A final-form field validator for a postcode that reads the country from another
 * field on the same form. Final-form passes every value as the second argument,
 * so the message follows the country dropdown without re-registering the field.
 *
 * @param {Object} messages `{ required, invalid, outsideUk }`, already translated.
 *   `required` may be left out where the fields are optional.
 * @param {String} countryField the form field holding the country code
 */
export const postcodeValidator = (messages, countryField) => (value, allValues) => {
  const isEmpty = !String(value || '').trim();
  if (isEmpty) {
    return messages.required || undefined;
  }
  const problem = postcodeProblemFor(value, allValues?.[countryField]);
  if (problem === 'outside-uk') {
    return messages.outsideUk;
  }
  if (problem === 'invalid') {
    return messages.invalid;
  }
  return undefined;
};
